import { useAccount, useConnectorClient } from "wagmi";
import {
  getAddressETHBalance,
  getProvider,
  getProviderNetwork,
  getWeb3,
} from "../services/contracts";
import { useState } from "react";
import { useEffect } from "react";
import {
  getUserKYCDetails,
  getUserKYCDetailsWeb3,
  getUserRole,
  getUserRoleWeb3,
} from "../services/contracts/clientContract";
import useUserStore from "../store/userSlice";
import useNetworkStore from "../store/networkStore";
import { ChainConfig, chainDetails, supportedChainsIds } from "../utils/config";
import { Provider, VerifiedWallet } from "@verified-network/verified-sdk";
import Web3 from "web3";
import { toMultichainNexusAccount } from "@biconomy/abstractjs";
import {
  base,
  baseSepolia,
  gnosis,
  mainnet,
  polygon,
  sepolia,
} from "viem/chains";
import { http } from "viem";
import { useWalletSelector } from "@near-wallet-selector/react-hook";
import { errorToast, kycAuthenticationChain } from "../utils/constants";
import { ethers } from "ethers";
import { fetchUserDetailsByExtenalUserId } from "../utils/helpers/kyc";
import { useConnector } from "@solana-commerce/connector";
import { fetchUserByUserId } from "../services/subraphs/walletSubraph";

export const useConnectWallet = (
  isMobileScreen,
  pageAction,
  setShowPopup,
  setPopupContentPosition,
  setPopupContent,
  setFavouriteClicked,
  setCustomLoading,
  setVerifiedWalletDetails,
  verifiedWalletDetails,
  custodyWalletDetails,
  verifiedWalletDetailsInt,
  setVerifiedWalletDetailsInt,
) => {
  const { address, chainId, isConnected } = useAccount();
  const { data: wagmiClient } = useConnectorClient({ chainId });
  const { signedAccountId, wallet } = useWalletSelector();
  const { accounts, selectedWallet } = useConnector();

  const { setNetwork, resetNetwork, name } = useNetworkStore();
  const { setUser, resetUser, userAddress } = useUserStore();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const connectAndFetchUserDetails = async () => {
      if (!address) {
        //overwrite last details to remove appKit wait time for last connector???
        localStorage.setItem(
          "@appkit/active_caip_network_id",
          `eip155:${chainDetails[0]?.chainId}`,
        );
        localStorage.removeItem("wagmi.recentConnectorId");
      }
      setLoading(true);
      setError(null);
      if (
        Object.values(verifiedWalletDetails)?.length === 0 &&
        Object.values(custodyWalletDetails)?.length === 0 &&
        Object.values(verifiedWalletDetailsInt)?.length === 0 &&
        !signedAccountId &&
        accounts?.length === 0
      ) {
        try {
          //step 1: clear user and network slice if wallet is disconnected. to track wallet disconnection
          if (!isConnected) {
            resetUser();
            resetNetwork();
          }

          if (
            isConnected &&
            address?.length > 0 &&
            pageAction !== "select-language"
          ) {
            setShowPopup(false);
            setPopupContentPosition("center");
            setPopupContent(null);
            if (setFavouriteClicked) {
              setFavouriteClicked(false);
            }
          }

          //step 2: get required network and user details from wagmi;
          let network, provider;
          if (wagmiClient?.chain) {
            network = getProviderNetwork(wagmiClient?.chain);
          }
          if (wagmiClient?.transport && network) {
            provider = getProvider(wagmiClient?.transport, network);
          }
          let signer, web3;
          if (provider && provider.getSigner && chainId && address) {
            signer = provider.getSigner(address);
            web3 = getWeb3(wagmiClient?.transport);
          }

          //step 3: update network and user slice on store and get more userDetails
          if (
            chainId > 0 &&
            address &&
            provider &&
            signer &&
            web3 &&
            supportedChainsIds.includes(chainId)
          ) {
            setUser({ name: "userAddress", value: address });
            setUser({ name: "isConnected", value: isConnected });
            setNetwork({ name: "chainId", value: chainId });

            setNetwork({ name: "name", value: wagmiClient?.chain?.name });

            setNetwork({ name: "provider", value: provider });
            setNetwork({ name: "signer", value: signer });
            //TODO: Update Verified Sdk to get rid of web3?? or move web3 to context or props?? to fix redux non serialized warning
            setNetwork({ name: "web3", value: web3 });

            const userRole = await getUserRole(chainId, address, signer);
            if (userRole?.length > 0) {
              setUser({ name: "userRole", value: userRole });
            }
            const nativeTokenBalance = await getAddressETHBalance(
              web3,
              address,
            );

            setUser({
              name: "userBalance",
              value: nativeTokenBalance,
            });

            const userKycDetails = await getUserKYCDetails(
              chainId,
              address,
              signer,
            );
            if (userKycDetails) {
              Object.keys(userKycDetails).forEach((key) => {
                setUser({
                  name: key,
                  value: userKycDetails[key],
                });
              });
            }
          }
        } catch (err) {
          if (
            !err.message?.includes(
              `Cannot read properties of undefined (reading 'chain')`,
            )
          ) {
            setError(
              err.message ||
                "Error while fetching user deatils. Try again later.",
            );
            console.error(
              "Error while connecting and fetching user details: ",
              err,
            );
          }
        } finally {
          setLoading(false);
        }
      } else if (
        Object.values(verifiedWalletDetails)?.length === 0 &&
        Object.values(custodyWalletDetails)?.length === 0 &&
        Object.values(verifiedWalletDetailsInt)?.length === 0 &&
        !signedAccountId &&
        accounts[0]?.address?.length > 0
      ) {
        const abiCoder = new ethers.utils.AbiCoder();
        setPopupContent(null);
        await fetchUserDetailsByExtenalUserId(
          abiCoder.encode(["string", "string"], [accounts[0]?.address, "397"]),
        ).then(async (res) => {
          setUser({ name: "userAddress", value: accounts[0]?.address });
          setNetwork({
            name: "signer",
            value: {
              ...selectedWallet?.features["solana:signMessage"],
              account: selectedWallet?.accounts[0],
            },
          });
          setUser({ name: "isConnected", value: true });
          setNetwork({ name: "name", value: "Solana" });

          if (res) {
            const userDt = await fetchUserByUserId(
              Number(kycAuthenticationChain),
              res?.id,
            );
            const userEvmAddress = userDt[0]?.id;
            if (userEvmAddress) {
              //if user address exist on the kyc authentication chain use address details
              const web3 = new Web3(
                ChainConfig[Number(kycAuthenticationChain)]?.rpcUrls[0],
              );
              const userRole = await getUserRoleWeb3(
                Number(kycAuthenticationChain),
                userEvmAddress,
                web3,
              );
              if (userRole?.length > 0) {
                setUser({ name: "userRole", value: userRole });
              }

              const userKycDetails = await getUserKYCDetailsWeb3(
                Number(kycAuthenticationChain),
                userEvmAddress,
                web3,
              );
              if (userKycDetails) {
                Object.keys(userKycDetails).forEach((key) => {
                  setUser({
                    name: key,
                    value: userKycDetails[key],
                  });
                });
              }
            } else {
              //if user address does not exist on the kyc authentication chain use sumsub details
              setUser({ name: "userFullName", value: res?.fullName });
              setUser({ name: "userEmail", value: res?.email });
              setUser({ name: "userCountry", value: res?.country });
              setUser({ name: "userType", value: res?.investorType });
              setUser({ name: "userFirstName", value: res?.firstName });
              setUser({ name: "userId", value: res?.id });
            }
            //set access token to always recover user KYC account????
            setUser({ name: "accessToken", value: res?.token?.token });
          }

          if (pageAction !== "select-language") {
            setShowPopup(false);
            setPopupContentPosition("center");

            if (setFavouriteClicked) {
              setFavouriteClicked(false);
            }
            setLoading(false);
          }
        });
      } else if (
        Object.values(verifiedWalletDetails)?.length === 0 &&
        Object.values(custodyWalletDetails)?.length === 0 &&
        Object.values(verifiedWalletDetailsInt)?.length === 0 &&
        signedAccountId &&
        accounts?.length === 0
      ) {
        const abiCoder = new ethers.utils.AbiCoder();
        setPopupContent(null);
        await fetchUserDetailsByExtenalUserId(
          abiCoder.encode(["string", "string"], [signedAccountId, "397"]),
        ).then(async (res) => {
          setUser({ name: "userAddress", value: signedAccountId });
          setNetwork({
            name: "signer",
            value: wallet,
          });
          setUser({ name: "isConnected", value: true });
          setNetwork({ name: "name", value: "Near" });
          if (res) {
            const userDt = await fetchUserByUserId(
              Number(kycAuthenticationChain),
              res?.id,
            );
            const userEvmAddress = userDt[0]?.id;
            if (userEvmAddress) {
              //if user address exist on the kyc authentication chain use address details
              const web3 = new Web3(
                ChainConfig[Number(kycAuthenticationChain)]?.rpcUrls[0],
              );
              const userRole = await getUserRoleWeb3(
                Number(kycAuthenticationChain),
                userEvmAddress,
                web3,
              );
              if (userRole?.length > 0) {
                setUser({ name: "userRole", value: userRole });
              }

              const userKycDetails = await getUserKYCDetailsWeb3(
                Number(kycAuthenticationChain),
                userEvmAddress,
                web3,
              );
              if (userKycDetails) {
                Object.keys(userKycDetails).forEach((key) => {
                  setUser({
                    name: key,
                    value: userKycDetails[key],
                  });
                });
              }
            } else {
              //if user address does not exist on the kyc authentication chain use sumsub details
              setUser({ name: "userFullName", value: res?.fullName });
              setUser({ name: "userEmail", value: res?.email });
              setUser({ name: "userCountry", value: res?.country });
              setUser({ name: "userType", value: res?.investorType });
              setUser({ name: "userFirstName", value: res?.firstName });
              setUser({ name: "userId", value: res?.id });
            }
            //set access token to always recover user KYC account????
            setUser({ name: "accessToken", value: res?.token?.token });
          }
          if (pageAction !== "select-language") {
            setShowPopup(false);
            setPopupContentPosition("center");

            if (setFavouriteClicked) {
              setFavouriteClicked(false);
            }
          }

          setLoading(false);
        });
      } else if (Object.values(verifiedWalletDetails)?.length > 0) {
        try {
          resetUser();
          resetNetwork();

          const _chainId = supportedChainsIds.includes(
            Number(verifiedWalletDetails?.chainId),
          )
            ? Number(verifiedWalletDetails?.chainId)
            : Number(chainDetails[0]?.chainId);

          //step 2: get required network and user details;
          let sender;
          if (
            verifiedWalletDetails?.pk &&
            verifiedWalletDetails?.pk?.startsWith("0x") &&
            _chainId > 0
          ) {
            if (
              verifiedWalletDetails?.address?.length > 0 &&
              pageAction !== "select-language"
            ) {
              setShowPopup(false);
              setPopupContentPosition("center");
              setPopupContent(null);
              if (setFavouriteClicked) {
                setFavouriteClicked(false);
              }
            }

            sender = new VerifiedWallet(verifiedWalletDetails?.pk);
          } else if (
            //disconnect on invalid/incomplete verifiedWalletDetails to allow other connections
            (verifiedWalletDetails?.address?.length > 0 &&
              !verifiedWalletDetails?.pk) ||
            (verifiedWalletDetails?.address?.length > 0 &&
              !verifiedWalletDetails?.pk?.startsWith("0x"))
          ) {
            setVerifiedWalletDetails({});
            localStorage.removeItem("VW:myVault");
          }

          //initialize provider, signer, privatekey and vault contract
          let provider, signer, web3;
          if (sender && ChainConfig[_chainId]?.rpcUrls) {
            provider = new Provider(
              ChainConfig[_chainId]?.rpcUrls[0],
              _chainId,
            );
          }

          if (provider) {
            signer = sender.setProvider(provider);
            web3 = new Web3(ChainConfig[_chainId]?.rpcUrls[0]);
          }

          //step 3: update network and user slice on store and get more userDetails
          if (
            sender &&
            verifiedWalletDetails?.address &&
            provider &&
            signer &&
            web3
          ) {
            setUser({
              name: "userAddress",
              value: verifiedWalletDetails?.address,
            });
            setUser({ name: "isConnected", value: true });

            setNetwork({
              name: "chainId",
              value: _chainId,
            });

            setNetwork({
              name: "name",
              value: ChainConfig[_chainId]?.chainName,
            });

            setNetwork({ name: "provider", value: provider });
            setNetwork({ name: "signer", value: signer });
            setNetwork({ name: "web3", value: web3 });

            const userRole = await getUserRoleWeb3(
              _chainId,
              verifiedWalletDetails?.address,
              web3,
            );
            if (userRole?.length > 0) {
              setUser({ name: "userRole", value: userRole });
            }
            const nativeTokenBalance = await getAddressETHBalance(
              web3,
              verifiedWalletDetails?.address,
            );

            setUser({
              name: "userBalance",
              value: nativeTokenBalance,
            });

            const userKycDetails = await getUserKYCDetailsWeb3(
              _chainId,
              verifiedWalletDetails?.address,
              web3,
            );
            if (userKycDetails) {
              Object.keys(userKycDetails).forEach((key) => {
                setUser({
                  name: key,
                  value: userKycDetails[key],
                });
              });
            }

            const chainToUse = [
              base,
              mainnet,
              gnosis,
              polygon,
              sepolia,
              baseSepolia,
            ].find((nt) => Number(nt?.id) === Number(_chainId));
            if (chainToUse) {
              const nexusAccount = await toMultichainNexusAccount({
                chains: [chainToUse],
                transports: [http(ChainConfig[_chainId]?.rpcUrls[0])],
                signer,
              });
              // console.log("nexus: ", nexusAccount);
              setNetwork({ name: "nexusAccount", value: nexusAccount });
            }

            localStorage.setItem(
              "@appkit/active_caip_network_id",
              `eip155:${_chainId}`,
            );
            localStorage.removeItem("wagmi.recentConnectorId");
          }
        } catch (err) {
          console.error(
            "Error while connecting and fetching user details: ",
            err,
          );
          setError(
            err.message ||
              "Error while fetching user deatils. Try again later.",
          );
        } finally {
          setLoading(false);
        }
      } else if (
        Object.values(verifiedWalletDetailsInt)?.length > 0 &&
        userAddress === "" &&
        name === ""
      ) {
        try {
          resetUser();
          resetNetwork();

          const _chainId = supportedChainsIds.includes(
            Number(verifiedWalletDetailsInt?.chainId),
          )
            ? Number(verifiedWalletDetailsInt?.chainId)
            : Number(chainDetails[0]?.chainId);

          //initialize provider, signer, privatekey and vault contract
          let provider, signer, web3;
          if (ChainConfig[_chainId]?.rpcUrls) {
            provider = new Provider(
              ChainConfig[_chainId]?.rpcUrls[0],
              _chainId,
            );
          }

          if (provider) {
            signer = provider.getSigner(verifiedWalletDetailsInt?.address);
            web3 = new Web3(ChainConfig[_chainId]?.rpcUrls[0]);
          }

          //step 3: update network and user slice on store and get more userDetails
          if (verifiedWalletDetailsInt?.address && provider && web3) {
            setUser({
              name: "userAddress",
              value: verifiedWalletDetailsInt?.address,
            });
            setUser({ name: "isConnected", value: true });

            setNetwork({
              name: "chainId",
              value: _chainId,
            });

            setNetwork({
              name: "name",
              value: ChainConfig[_chainId]?.chainName,
            });

            setNetwork({ name: "provider", value: provider });
            setNetwork({ name: "signer", value: signer });
            setNetwork({ name: "web3", value: web3 });

            const userRole = await getUserRoleWeb3(
              _chainId,
              verifiedWalletDetailsInt?.address,
              web3,
            );
            if (userRole?.length > 0) {
              setUser({ name: "userRole", value: userRole });
            }
            const nativeTokenBalance = await getAddressETHBalance(
              web3,
              verifiedWalletDetailsInt?.address,
            );

            setUser({
              name: "userBalance",
              value: nativeTokenBalance,
            });

            const userKycDetails = await getUserKYCDetailsWeb3(
              _chainId,
              verifiedWalletDetailsInt?.address,
              web3,
            );
            if (userKycDetails) {
              Object.keys(userKycDetails).forEach((key) => {
                setUser({
                  name: key,
                  value: userKycDetails[key],
                });
              });
            }
          }
        } catch (err) {
          console.error(
            "Error while connecting and fetching user details: ",
            err,
          );
          setError(
            err.message ||
              "Error while fetching user deatils. Try again later.",
          );
        } finally {
          setLoading(false);
        }
      } else if (Object.values(custodyWalletDetails)?.length > 0) {
        try {
          resetUser();
          resetNetwork();

          const _chainId = supportedChainsIds.includes(
            Number(custodyWalletDetails?.chainId),
          )
            ? Number(custodyWalletDetails?.chainId)
            : Number(chainDetails[0]?.chainId);

          //step 2: get required network and user details from wagmi;
          let sender;
          if (
            custodyWalletDetails?.pk &&
            custodyWalletDetails?.pk?.startsWith("0x") &&
            _chainId > 0
          ) {
            sender = new VerifiedWallet(custodyWalletDetails?.pk);
          }

          //initialize provider, signer, privatekey and vault contract
          let provider, signer, web3;
          if (sender && ChainConfig[_chainId]?.rpcUrls) {
            provider = new Provider(
              ChainConfig[_chainId]?.rpcUrls[0],
              _chainId,
            );
          }

          if (provider) {
            signer = sender.setProvider(provider);
            web3 = new Web3(ChainConfig[_chainId]?.rpcUrls[0]);
          }

          //step 3: update network and user slice on store and get more userDetails
          if (
            sender &&
            custodyWalletDetails?.address &&
            provider &&
            signer &&
            web3
          ) {
            setUser({
              name: "userAddress",
              value: custodyWalletDetails?.address,
            });
            setUser({ name: "isConnected", value: true });

            setNetwork({
              name: "chainId",
              value: _chainId,
            });

            setNetwork({
              name: "name",
              value: ChainConfig[_chainId]?.chainName,
            });

            setNetwork({ name: "provider", value: provider });
            setNetwork({ name: "signer", value: signer });
            setNetwork({ name: "web3", value: web3 });

            const userRole = await getUserRoleWeb3(
              _chainId,
              custodyWalletDetails?.address,
              web3,
            );
            if (userRole?.length > 0) {
              setUser({ name: "userRole", value: userRole });
            }
            const nativeTokenBalance = await getAddressETHBalance(
              web3,
              custodyWalletDetails?.address,
            );

            setUser({
              name: "userBalance",
              value: nativeTokenBalance,
            });

            const userKycDetails = await getUserKYCDetailsWeb3(
              _chainId,
              custodyWalletDetails?.address,
              web3,
            );
            if (userKycDetails) {
              Object.keys(userKycDetails).forEach((key) => {
                setUser({
                  name: key,
                  value: userKycDetails[key],
                });
              });
            }

            if (pageAction !== "select-language") {
              setShowPopup(false);
              setPopupContentPosition("center");
              setPopupContent(null);
              if (setFavouriteClicked) {
                setFavouriteClicked(false);
              }
            }
          }
        } catch (err) {
          console.error(
            "Error while connecting and fetching user details: ",
            err,
          );
          setError(
            err.message ||
              "Error while fetching user deatils. Try again later.",
          );
        } finally {
          setLoading(false);
        }
      }
    };

    connectAndFetchUserDetails();
  }, [
    address,
    isConnected,
    chainId,
    wagmiClient,
    signedAccountId,
    isMobileScreen,
    pageAction,
    setShowPopup,
    setPopupContentPosition,
    setPopupContent,
    setFavouriteClicked,
    setCustomLoading,
    setVerifiedWalletDetails,
    verifiedWalletDetails,
    custodyWalletDetails,
    resetUser,
    resetNetwork,
    setUser,
    setNetwork,
    verifiedWalletDetailsInt,
    userAddress,
    name,
    accounts,
    selectedWallet?.features,
    selectedWallet?.accounts,
    wallet,
  ]);

  return { connectWalletLoading: loading, connectWalletError: error };
};
